import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  Search, BookOpen, LifeBuoy, MessageSquare, Ticket, Activity, PlayCircle,
  ChevronRight, Clock, Eye, ListChecks, FolderOpen, ArrowLeft, Bug, Lightbulb,
} from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import SetupChecklist from "@/components/SetupChecklist";
import { api } from "@/lib/api";

const ActionCard = ({ icon: Icon, title, desc, to, testid, soon }) => {
  const inner = (
    <div className={`rounded-xl border border-line bg-bg-surface p-4 h-full transition ${soon ? "opacity-60" : "hover:border-brand-amber/50 hover:bg-bg-elevated cursor-pointer"}`}>
      <Icon className="h-5 w-5 text-brand-amber mb-2" />
      <div className="text-sm font-semibold text-ink-primary flex items-center gap-2">
        {title}
        {soon && <span className="font-mono text-[8px] uppercase tracking-wider text-ink-muted border border-line rounded px-1.5 py-0.5">Coming soon</span>}
      </div>
      <div className="text-[11px] text-ink-muted mt-1">{desc}</div>
    </div>
  );
  return soon ? <div data-testid={testid}>{inner}</div> : <Link to={to} data-testid={testid}>{inner}</Link>;
};

const ArticleRow = ({ a }) => (
  <Link to={`/dashboard/help/article/${a.slug}`} data-testid={`help-article-${a.slug}`}
    className="flex items-center justify-between gap-3 rounded-lg border border-line bg-bg-surface px-4 py-3 hover:border-brand-amber/40 hover:bg-bg-elevated transition group">
    <div className="min-w-0">
      <div className="text-sm text-ink-primary group-hover:text-brand-amber transition truncate">{a.title}</div>
      <div className="text-[11px] text-ink-muted truncate">{a.description}</div>
      <div className="flex items-center gap-3 mt-1 font-mono text-[9px] uppercase tracking-wider text-ink-muted">
        <span>{a.category}</span>
        <span className="inline-flex items-center gap-1"><Clock className="h-2.5 w-2.5" />{a.read_minutes} min read</span>
        {a.updated_at && <span>Updated {new Date(a.updated_at).toLocaleDateString()}</span>}
        {a.relevance != null && <span className="text-brand-amber">Relevance {a.relevance}</span>}
      </div>
    </div>
    <ChevronRight className="h-4 w-4 text-ink-muted shrink-0" />
  </Link>
);

export default function HelpCenter() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const category = params.get("category") || "";
  const [home, setHome] = useState(null);
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [catArticles, setCatArticles] = useState(null);
  const debounce = useRef(null);

  useEffect(() => { api.get("/help/home").then((r) => setHome(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    if (!category) { setCatArticles(null); return; }
    api.get(`/help/articles?category=${encodeURIComponent(category)}`)
      .then((r) => setCatArticles(r.data.articles)).catch(() => setCatArticles([]));
  }, [category]);

  const runSearch = (term) => {
    if (term.trim().length < 2) { setResults(null); return; }
    setSearching(true);
    api.get(`/help/search?q=${encodeURIComponent(term)}`)
      .then((r) => setResults(r.data.results))
      .catch(() => setResults([]))
      .finally(() => setSearching(false));
  };
  const onQ = (v) => {
    setQ(v);
    clearTimeout(debounce.current);
    debounce.current = setTimeout(() => runSearch(v), 350);
  };

  return (
    <div className="space-y-8" data-testid="help-center-page">
      {/* Hero */}
      <div className="rounded-2xl border border-line bg-bg-surface p-6 sm:p-10 text-center">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-brand-amber mb-3">// Help & Support</div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold text-ink-primary mb-2">How can we help you?</h1>
        <p className="text-sm text-ink-secondary max-w-lg mx-auto mb-6">
          Search the BroadcastFlow AI Help Center, watch setup tutorials, or contact our support team.
        </p>
        <div className="relative max-w-xl mx-auto">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-muted" />
          <input value={q} onChange={(e) => onQ(e.target.value)} data-testid="help-search-input"
            placeholder="Search articles, features, errors…"
            className="w-full rounded-full border border-line bg-bg px-11 py-3 text-sm text-ink-primary placeholder:text-ink-muted focus:outline-none focus:border-brand-amber/60 transition" />
        </div>
        {home?.suggested_searches && !results && (
          <div className="flex flex-wrap justify-center gap-2 mt-4">
            {home.suggested_searches.map((s) => (
              <button key={s} type="button" onClick={() => { setQ(s); runSearch(s); }}
                data-testid={`help-suggested-${s.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className="rounded-full border border-line bg-bg-elevated px-3 py-1 text-[11px] text-ink-secondary hover:text-brand-amber hover:border-brand-amber/40 transition">
                {s}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Search results */}
      {results !== null && (
        <section data-testid="help-search-results">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-mono text-[10px] uppercase tracking-wider text-ink-muted">
              // {searching ? "Searching…" : `${results.length} result${results.length === 1 ? "" : "s"} for "${q}"`}
            </h2>
            <button type="button" onClick={() => { setQ(""); setResults(null); }}
              className="text-[11px] text-ink-muted hover:text-ink-primary">Clear</button>
          </div>
          <div className="space-y-2">
            {results.map((a) => <ArticleRow key={a.slug} a={a} />)}
            {!searching && results.length === 0 && (
              <div className="rounded-lg border border-line bg-bg-surface px-4 py-6 text-center text-sm text-ink-muted">
                No articles matched. Try different words, or <Link to="/dashboard/support/new" className="text-brand-amber hover:underline">submit a support ticket</Link>.
              </div>
            )}
          </div>
        </section>
      )}

      {/* Category browse view */}
      {category && !results && (
        <section data-testid="help-category-view">
          <button type="button" onClick={() => setParams({})}
            className="inline-flex items-center gap-1.5 text-xs text-ink-muted hover:text-ink-primary mb-3">
            <ArrowLeft className="h-3.5 w-3.5" /> All categories
          </button>
          <h2 className="font-display text-xl font-bold text-ink-primary mb-4">{category}</h2>
          <div className="space-y-2">
            {(catArticles || []).map((a) => <ArticleRow key={a.slug} a={a} />)}
            {catArticles && catArticles.length === 0 && (
              <div className="text-sm text-ink-muted">No published articles in this category yet.</div>
            )}
          </div>
        </section>
      )}

      {!results && !category && (
        <>
          {/* Quick actions */}
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
            <ActionCard icon={ListChecks} title="Getting Started" desc="Guided tour, setup guides, and Beginner Mode" to="/dashboard/help/getting-started" testid="help-action-getting-started" />
            <ActionCard icon={Ticket} title="Submit a ticket" desc="Report a problem or ask a question" to="/dashboard/support/new" testid="help-action-new-ticket" />
            <ActionCard icon={MessageSquare} title="My support tickets" desc="Track your open conversations" to="/dashboard/support/tickets" testid="help-action-my-tickets" />
            <ActionCard icon={Bug} title="Report a problem" desc="Structured bug report with diagnostics" to="/dashboard/support/bug" testid="help-action-bug" />
            <ActionCard icon={Lightbulb} title="Request a feature" desc="Tell us what to build next" to="/dashboard/support/feature-request" testid="help-action-feature" />
            <ActionCard icon={LifeBuoy} title="Contact support" desc="Support email and hours" to="/dashboard/help/contact" testid="help-action-contact" />
            <ActionCard icon={Activity} title="System status" desc="Live platform component status" to="/status" testid="help-action-status" />
          </div>

          {/* Getting started checklist */}
          <SetupChecklist />

          {/* Popular articles */}
          {home?.popular?.length > 0 && (
            <section>
              <h2 className="font-mono text-[10px] uppercase tracking-wider text-ink-muted mb-3">// Popular help articles</h2>
              <div className="space-y-2" data-testid="help-popular-articles">
                {home.popular.map((a) => <ArticleRow key={a.slug} a={a} />)}
              </div>
            </section>
          )}

          {/* Categories */}
          <section>
            <h2 className="font-mono text-[10px] uppercase tracking-wider text-ink-muted mb-3">// Browse by category</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2" data-testid="help-categories">
              {(home?.categories || []).filter((c) => c.count > 0).map((c) => (
                <button key={c.name} type="button" onClick={() => setParams({ category: c.name })}
                  data-testid={`help-category-${c.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  className="flex items-center gap-2 rounded-lg border border-line bg-bg-surface px-3 py-2.5 text-left hover:border-brand-amber/40 transition">
                  <FolderOpen className="h-4 w-4 text-brand-amber shrink-0" />
                  <span className="text-xs text-ink-primary truncate">{c.name}</span>
                  <span className="ml-auto font-mono text-[9px] text-ink-muted">{c.count}</span>
                </button>
              ))}
            </div>
          </section>

          {/* Video tutorials */}
          {home?.videos?.length > 0 && (
            <section>
              <div className="flex items-center justify-between mb-3">
                <h2 className="font-mono text-[10px] uppercase tracking-wider text-ink-muted">// Video tutorials</h2>
                <Link to="/dashboard/help/videos" data-testid="help-videos-view-all"
                  className="text-[11px] text-brand-amber hover:underline">View full library →</Link>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="help-videos">
                {home.videos.slice(0, 8).map((v) => (
                  <div key={v.video_id} className="rounded-xl border border-line bg-bg-surface overflow-hidden">
                    <div className="aspect-video bg-bg-elevated flex items-center justify-center relative">
                      {v.video_url ? (
                        <a href={v.video_url} target="_blank" rel="noreferrer" className="absolute inset-0 flex items-center justify-center hover:bg-black/20 transition">
                          <PlayCircle className="h-9 w-9 text-brand-amber" />
                        </a>
                      ) : (
                        <div className="text-center">
                          <PlayCircle className="h-8 w-8 text-ink-muted mx-auto" />
                          <div className="font-mono text-[8px] uppercase tracking-wider text-ink-muted mt-1">Video coming soon</div>
                        </div>
                      )}
                    </div>
                    <div className="p-3">
                      <div className="text-xs text-ink-primary font-semibold leading-snug">{v.title}</div>
                      <div className="font-mono text-[9px] uppercase tracking-wider text-ink-muted mt-1">{v.difficulty}{v.duration ? ` · ${v.duration}` : ""}</div>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* FAQs */}
          {home?.faqs?.length > 0 && (
            <section>
              <h2 className="font-mono text-[10px] uppercase tracking-wider text-ink-muted mb-3">// Frequently asked questions</h2>
              <Accordion type="single" collapsible className="rounded-xl border border-line bg-bg-surface px-4" data-testid="help-faqs">
                {home.faqs.map((f) => (
                  <AccordionItem key={f.faq_id} value={f.faq_id} className="border-line">
                    <AccordionTrigger className="text-sm text-ink-primary text-left hover:no-underline hover:text-brand-amber">
                      {f.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-ink-secondary">
                      {f.answer}
                      {f.related_slug && (
                        <Link to={`/dashboard/help/article/${f.related_slug}`} className="block mt-2 text-xs text-brand-amber hover:underline">
                          Read the full guide →
                        </Link>
                      )}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </section>
          )}
        </>
      )}
    </div>
  );
}
